/**
 * WhatsApp Markdown Parser
 *
 * Converts WhatsApp-style markdown to React Native Text components.
 * Supports: *bold*, _italic_, ~strikethrough~, ```code```
 */

import React from 'react';
import { Text, StyleSheet, TextStyle } from 'react-native';

interface Segment {
  text: string;
  style?: TextStyle;
}

const styles = StyleSheet.create({
  bold: { fontWeight: 'bold' as const },
  italic: { fontStyle: 'italic' as const },
  strikethrough: { textDecorationLine: 'line-through' as const },
  code: { fontFamily: 'monospace', backgroundColor: '#f0f0f0', paddingHorizontal: 4 },
});

// Pre-compiled patterns — avoid recreating regex objects on every render
const PATTERNS: ReadonlyArray<{ regex: RegExp; style: TextStyle }> = [
  { regex: /\*([^*]+)\*/g, style: styles.bold },
  { regex: /_([^_]+)_/g, style: styles.italic },
  { regex: /~([^~]+)~/g, style: styles.strikethrough },
  { regex: /```([^`]+)```/g, style: styles.code },
];

/**
 * Parse WhatsApp-style markdown into styled segments.
 */
export function parseWhatsAppMarkdown(text: string): Segment[] {
  const segments: Segment[] = [];
  let remaining = text;

  // Simple approach: split by patterns one by one
  // For complex nested markdown, a proper parser would be better
  let i = 0;
  while (i < remaining.length) {
    let matched = false;

    for (const { regex, style } of PATTERNS) {
      regex.lastIndex = i;
      const match = regex.exec(remaining);
      if (match && match.index === i) {
        segments.push({ text: match[1], style });
        i = match.index + match[0].length;
        matched = true;
        break;
      }
    }

    if (!matched) {
      // Find next pattern match
      let nextMatch = remaining.length;
      for (const { regex } of PATTERNS) {
        regex.lastIndex = i;
        const match = regex.exec(remaining);
        if (match && match.index < nextMatch) {
          nextMatch = match.index;
        }
      }
      segments.push({ text: remaining.slice(i, nextMatch) });
      i = nextMatch;
    }
  }

  return segments;
}

/**
 * Render parsed markdown as React Native Text elements.
 */
export function renderWhatsAppMarkdown(text: string, baseStyle?: TextStyle): React.ReactNode {
  const segments = parseWhatsAppMarkdown(text);
  if (segments.length === 1 && !segments[0].style) {
    return React.createElement(Text, { style: baseStyle }, segments[0].text);
  }
  return React.createElement(
    Text,
    { style: baseStyle },
    segments.map((seg, idx) =>
      seg.style
        ? React.createElement(Text, { key: idx, style: seg.style }, seg.text)
        : seg.text
    )
  );
}
